import React, { useCallback, useRef, useState, useEffect } from "react";
import { makePost, makePatch } from "@culturehq/client";
import { Button, Form, Icon, ModalDialog, StringField, SubmitButton, Thumbnail } from "@culturehq/components";
import * as CHQ from "chq";

import { useProfile } from "app/context";
import { useDoor } from "app/hooks";
import { getName, t, Trans } from "app/utils";

import AccessibleFocusOutlineElement from "components/common/AccessibleFocusOutlineElement";
import LazyEditor from "components/editor/LazyEditor";
import Editor from "components/editor/Editor";
import SlackChannelField, { noSlackBroadcastValue } from "components/event/edit/SlackChannelField";
import { MENTION_VALUE, SEGMENTED_VALUE } from "constants/mentionConstants";
import { getIcon } from "constants/tagsTypes";
import SelectTags from "./post/SelectTags";
import ChqModalDialog from "./common/ChqModalDialog";
import useAddTags from "./stories/post-stories-v2/hooks/useAddTags";

// lololol this needs to be extracted and put into an export on the component
// library, but at the moment I'm trying to get this out
type FormValues = Parameters<React.ComponentProps<typeof Form>["onSubmit"]>[0];

const defaultPost = {
  title: null,
  body: null
};

type PostFormProps = {
  editorRef: React.RefObject<Editor>;
  post: CHQ.Post | typeof defaultPost;
  onChange: () => void;
  onShareWithChanged: (_newOptions: Array<any>) => void;
  onSubmit: (_values: FormValues) => Promise<void>;
  openImageLibrary: boolean;
  selectedOptions: Array<any>;
};

const PostForm: React.FC<PostFormProps> = ({
  editorRef,
  post,
  onChange,
  onShareWithChanged,
  onSubmit,
  openImageLibrary,
  selectedOptions
}) => {
  const [editorPadding, setEditorPadding] = useState({ paddingBottom: "55px" });

  const handleShareWithChanged = (newOptions: any, selectHeight: number) => {
    onShareWithChanged(newOptions);
    setEditorPadding({ paddingBottom: `${selectHeight + 35}px` });
  };

  return (
    <Form onSubmit={onSubmit} initialValues={{ title: post.title }} className="form">
      <StringField name="title" placeholder="Choose a title for your post" required onChange={onChange} autoFocus>
        {" "}
      </StringField>
      <div className="chq-ffd">
        <LazyEditor
          forzeOpen
          ref={editorRef}
          initialValue={post.body}
          placeholder={t("Share a message with a photo...")}
          onChange={onChange}
          style={editorPadding}
          isPostModal
          openImageLibrary={openImageLibrary}
        />
        <SelectTags
          ariaLabel={t("Search tags to personalize your update")}
          placeholder={t("Add tags to personalize your update")}
          selectedOptions={selectedOptions}
          setEditorPadding={setEditorPadding}
          onShareWithChanged={handleShareWithChanged}
          withOrgValues
        />
      </div>
      <SubmitButton primary>
        {submitting => (
          submitting ? <Trans>Submitting...</Trans> : <Trans>Post Update</Trans>
        )}
      </SubmitButton>
    </Form>
  );
};

type PostButtonProps = {
  isAButton: boolean;
  onOpen: () => void;
  post?: CHQ.Post;
  profileName: string;
  placeholder?: string;
  withIcon: boolean;
};

const PostButton: React.FC<PostButtonProps> = ({
  isAButton,
  onOpen,
  post,
  profileName,
  placeholder,
  withIcon
}) => {
  if (post) {
    return (
      <Button inverted primary onClick={onOpen}>
        <Icon icon="edit" /> <Trans>Edit</Trans>
      </Button>
    );
  }

  if (isAButton) {
    return (
      <Button inverted onClick={onOpen} aria-label={t("Add a photo")}>
        <Icon icon="image-outlined" />
      </Button>
    );
  }

  if (placeholder) {
    return (
      <AccessibleFocusOutlineElement tag="button" className="FeedItem__preview__title transparent-button" onClick={onOpen}>
        {withIcon && <Icon icon="post" />}
        <span>{placeholder}</span>
      </AccessibleFocusOutlineElement>
    );
  }

  return (
    <AccessibleFocusOutlineElement tag="button" className="FeedItem__preview__title transparent-button" onClick={onOpen}>
      <span><Trans>Hi {profileName}, share an update or photo</Trans></span>
    </AccessibleFocusOutlineElement>
  );
};

type PostModalProps = {
  initialOpen?: boolean;
  isAButton?: boolean;
  onPosted?: (_post: CHQ.Post) => void;
  openImageLibrary?: boolean;
  post?: CHQ.Post;
  placeholder?: string;
  prefilledOptions?: Array<any>;
};

const PostModal: React.FC<PostModalProps> = ({
  initialOpen = false,
  isAButton = false,
  onPosted = () => {},
  openImageLibrary = false,
  post = undefined,
  placeholder,
  prefilledOptions = undefined
}) => {
  const editorRef = useRef<Editor>(null);
  const { profile: rawProfile } = useProfile();

  // This shouldn't be necessary but it is because currently useProfile is lying
  // about its state. In reality it's not a union, it's just a Profile, because
  // if we've gotten this far we're definitely logged in.
  const profile = rawProfile as CHQ.Profile;
  const [changed, setChanged] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(noSlackBroadcastValue);
  const [selectedOptions, setSelectedOptions] = useState<Array<any>>([]);
  const handleChange = useCallback(() => setChanged(true), [setChanged]);
  const addTags = useAddTags({ tags: selectedOptions });
  const disabledSlackChannel = !!post;

  const { open, onOpen, onClose } = useDoor(initialOpen);

  const restartState = useCallback(
    () => {
      if (!post) {
        setSelectedOptions([]);
        setChanged(false);
      }

      if (prefilledOptions) {
        setSelectedOptions(prefilledOptions);
      }
    },
    [post, prefilledOptions]
  );

  const handleConfirmClose = useCallback(
    () => {
      const message = t("You have unsaved changes, are you sure you'd like to close the post?");

      // eslint-disable-next-line no-alert
      if (!changed || window.confirm(message)) {
        onClose();
        restartState();
      }
    },
    [changed, onClose, restartState]
  );

  const getResponse = useCallback(
    async (title: string, body: string, slack_channel: string) => {
      let response;
      if (post) {
        response = await makePatch<CHQ.UpdatePost>(`/posts/${post.id}`, { title, body });
      } else {
        response = await makePost<CHQ.CreatePost>("/posts", { title, body, slack_channel });
      }

      onClose();
      restartState();
      onPosted(response.post);
    },
    [onClose, onPosted, post, restartState]
  );

  const handleAWSUploadedFile = useCallback(
    async (title: string, body: string, channel: string) => {
      await getResponse(title, body, channel);
    },
    [getResponse]
  );

  const waitForS3 = useCallback(
    async (ms: number, title: string, body: string, channel: string) => (
      new Promise(_ => {
        // This timeout is to allow AWS to upload the file, in this point,
        // AWs has responded with a 200 status, but we need to take care of
        // S3 Consistency Model
        setTimeout(handleAWSUploadedFile, ms, title, body, channel);
      })
    ),
    [handleAWSUploadedFile]
  );

  const handleSubmit = useCallback(
    async ({ title }) => {
      let body = "";
      if (editorRef.current) {
        body = await editorRef.current.serialize();
        const bodyWithTags = addTags(body);
        await waitForS3(2000, title, bodyWithTags, selectedChannel);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editorRef, selectedChannel, selectedOptions, waitForS3, addTags]
  );

  const handleShareWithChanged = (newOptions: Array<any>) => {
    setChanged(true);
    setSelectedOptions(newOptions);
  };

  const handleSelectedChannel = (channel: string) => {
    setSelectedChannel(channel);
  };

  useEffect(() => {
    const getSelectedOptions = () => {
      const postOptions: any[] = [];
      if (post) {
        const body = JSON.parse(post.body);
        Object.entries(body.entityMap).forEach(([_, value]) => {
          const { mutability, type, data } = value as any;
          if (mutability === SEGMENTED_VALUE
            && type === MENTION_VALUE
            && data.type
          ) {
            postOptions.push({
              category: data.type,
              value: data.entityId,
              label: data.name,
              categoryIcon: getIcon(data.type)
            });
          }
        });
      }

      return postOptions;
    };

    const opts = getSelectedOptions();
    setSelectedOptions(opts);
  }, [post]);

  useEffect(() => {
    if (prefilledOptions) {
      setSelectedOptions(prefilledOptions);
    }
  }, [prefilledOptions]);

  return (
    <>
      {open && (
        <ChqModalDialog entrance="zoomIn" onClose={handleConfirmClose} className="post-update-modal" ariaLabelledBy="post-update-title" ariaDescribedby="post-update-title">
          <ModalDialog.Heading titleId="post-update-title" onClose={handleConfirmClose}>
            <Trans>Post Update</Trans>
          </ModalDialog.Heading>
          <ModalDialog.Body>
            <Thumbnail image={profile.avatar.thumbUrl} />
            <PostForm
              editorRef={editorRef}
              post={post || defaultPost}
              onChange={handleChange}
              onShareWithChanged={handleShareWithChanged}
              onSubmit={handleSubmit}
              openImageLibrary={openImageLibrary}
              selectedOptions={selectedOptions}
            />
          </ModalDialog.Body>
          <ModalDialog.Footer>
            <SlackChannelField
              className="modal-footer-column"
              disabled={disabledSlackChannel}
              imageIconPath="/slack.png"
              showLabel={false}
              onSelectedChannel={handleSelectedChannel}
              channel={post?.slackChannel}
            />
            <div className="modal-footer-column" />
          </ModalDialog.Footer>
        </ChqModalDialog>
      )}
      <PostButton
        isAButton={isAButton}
        onOpen={onOpen}
        post={post}
        profileName={getName(profile)}
        placeholder={placeholder}
        withIcon={(prefilledOptions && prefilledOptions.length > 0) || false}
      />
    </>
  );
};

export default PostModal;
